import React from "react";
import logo from "../assets/images/logo-transparent.png";
import usePageMeta from "../hooks/usePageMeta";
import useScrollReveal from "../hooks/useScrollReveal";

const profilePoints = [
  {
    lead: "Polyvalent",
    text: "une expérience aussi bien en frontend qu’en backend, de l’intégration à la mise en production.",
  },
  {
    lead: "Pragmatique",
    text: "je privilégie les solutions simples, maintenables et adaptées au besoin plutôt que la complexité inutile.",
  },
  {
    lead: "Autonome",
    text: "je suis à l’aise pour analyser un sujet, rechercher une solution et faire avancer un projet jusqu’à sa réalisation.",
  },
  {
    lead: "Curieux",
    text: "j’aime comprendre le fonctionnement des outils que j’utilise et continuer à faire évoluer mes pratiques.",
  },
];

const projectContributions = [
  {
    lead: "Une vision globale du développement web",
    text: "interface, logique métier, API, base de données, déploiement et maintenance.",
  },
  {
    lead: "Une attention particulière à l’utilisateur",
    text: "une application doit être agréable à utiliser, compréhensible et répondre à un besoin concret.",
  },
  {
    lead: "De la rigueur",
    text: "qualité du code, lisibilité, tests, versioning et attention portée aux régressions.",
  },
  {
    lead: "Une vraie capacité d’adaptation",
    text: "projets existants, refontes, nouvelles applications, environnements techniques et métiers différents.",
  },
  {
    lead: "Un bon esprit d’équipe",
    text: "j’aime échanger, partager les décisions techniques et avancer collectivement vers une solution efficace.",
  },
];

function HighlightList({ items }) {
  return (
    <ul className="space-y-4 pl-0">
      {items.map((item) => (
        <li key={item.lead} className="flex items-start gap-3">
          <span
            className="mt-[0.65rem] h-2 w-2 shrink-0 rounded-full bg-brandStrong"
            aria-hidden="true"
          />
          <span className="min-w-0">
            <strong>{item.lead}</strong> — {item.text}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AboutPage() {
  usePageMeta({
    title: "À propos",
    description:
      "Découvrez le parcours, les valeurs et la vision de Sofiane Amroune, développeur web sénior polyvalent, du frontend au backend.",
  });

  const { ref: heroRef, revealProps: heroRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: contentRef, revealProps: contentRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.12,
  });

  const { ref: visionRef, revealProps: visionRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.15,
  });

  return (
    <div className="page-shell space-y-8 lg:space-y-10">
      <section
        ref={heroRef}
        {...heroRevealProps}
        className="hero-panel scroll-reveal"
      >
        <div className="grid items-center gap-8 lg:grid-cols-[1.15fr,0.85fr]">
          <div>
            <span className="eyebrow">À propos</span>
            <h1 className="hero-title">À propos de moi</h1>
            <p className="hero-lead">
              Je m’appelle Sofiane et je suis <strong>développeur web sénior</strong>, avec plusieurs
              années d’expérience dans la conception, l’évolution et la maintenance d’applications web.
            </p>
            <p className="hero-copy">
              J’interviens aussi bien sur le <strong>frontend</strong> que sur le <strong>backend</strong>,
              avec une appétence particulière pour les interfaces modernes, l’expérience utilisateur et
              les environnements <strong>React / TypeScript</strong>. Mon parcours m’a également amené à
              travailler avec <strong>PHP / Symfony</strong>, <strong>Node.js</strong>, <strong>PostgreSQL</strong>,
              <strong> Docker</strong> et les outils <strong>CI/CD</strong>.
            </p>
            <p className="hero-copy">
              Au-delà de la technique, j’aime surtout comprendre un besoin, trouver une solution pertinente
              et construire des applications <strong>claires, fiables et réellement utiles</strong>. J’accorde
              autant d’importance à la qualité du code qu’à l’expérience proposée à l’utilisateur final.
            </p>
          </div>

          <div className="hidden justify-center lg:flex lg:justify-end">
            <div className="flex h-[17rem] w-[17rem] items-center justify-center rounded-full border border-brand/20 bg-white/85 shadow-panel sm:h-[19rem] sm:w-[19rem]">
              <img
                src={logo}
                alt="Logo Sofiane Amroune"
                className="h-40 w-40 object-contain sm:h-48 sm:w-48"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        ref={contentRef}
        {...contentRevealProps}
        className="scroll-reveal grid gap-6 lg:grid-cols-2"
      >
        <article className="section-card">
          <span className="eyebrow">Profil</span>
          <h2 className="mb-5 text-3xl">Mon profil en quelques mots</h2>
          <HighlightList items={profilePoints} />
        </article>

        <article className="section-card-soft">
          <span className="eyebrow">Apports</span>
          <h2 className="mb-5 text-3xl">Ce que j’apporte à un projet</h2>
          <HighlightList items={projectContributions} />
        </article>
      </section>

      <section
        ref={visionRef}
        {...visionRevealProps}
        className="scroll-reveal grid gap-6 lg:grid-cols-[0.9fr,1.1fr]"
      >
        <article className="section-card-soft">
          <span className="eyebrow">Expérience</span>
          <h2 className="mb-5 text-3xl">Ce que mon parcours m’a appris</h2>
          <p>
            Avec les années et les projets, j’ai appris qu’une bonne solution technique n’est pas
            forcément la plus complexe.
          </p>
          <p className="mt-4">
            <strong>Comprendre le besoin</strong>, <strong>écouter les utilisateurs</strong> et
            <strong> savoir faire des choix</strong> sont aussi importants que la maîtrise d’un framework
            ou d’un langage.
          </p>
          <p className="mt-4">
            Mon expérience sur des applications existantes, des refontes et des projets développés de zéro
            m’a également appris à m’adapter rapidement, à travailler avec différentes contraintes et à
            toujours garder en tête la <strong>maintenabilité du projet sur le long terme</strong>.
          </p>
        </article>

        <article className="section-card">
          <span className="eyebrow">Méthode</span>
          <h2 className="mb-5 text-3xl">Ma façon de travailler</h2>
          <p>
            J’aime partir d’un besoin concret, comprendre son contexte puis construire progressivement une
            solution <strong>simple, robuste et cohérente</strong>.
          </p>
          <p className="mt-4">
            Je porte une attention particulière à la <strong>qualité de l’interface</strong>, à la
            <strong> lisibilité du code</strong> et à la capacité du projet à évoluer dans le temps.
          </p>
          <p className="mt-4">
            Mon objectif n’est pas simplement de développer une fonctionnalité, mais de produire quelque
            chose de <strong>fiable, utile et agréable à maintenir comme à utiliser</strong>.
          </p>
        </article>
      </section>
    </div>
  );
}

export default AboutPage;
